import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
  TopUrlByLineItemRow,
  LineItemMetricRow,
} from '@monitoring-panel/types';
import { ReportTable, type ReportColumn } from './ReportTable';
import { formatCount, formatInt, formatPercent } from '@/lib/report-format';

interface ReportLineItemPerformanceProps {
  /** Top URLs per line item (≤20 each), the granular performance data. */
  topUrls: TopUrlByLineItemRow[];
  /** Aggregated line item KPIs, used for the per-line-item header stats. */
  lineItems?: LineItemMetricRow[];
}

interface LineItemGroup {
  lineItemId: string;
  insertionOrderId: string;
  urls: TopUrlByLineItemRow[];
  totalImpressions: number;
}

const urlColumns: ReportColumn<TopUrlByLineItemRow>[] = [
  { key: 'rank', label: '#', render: (r) => <span className="adv-num text-adv-tertiary">#{r.rank}</span> },
  {
    key: 'url',
    label: 'URL',
    wrap: true,
    render: (r) => (
      <div className="min-w-0">
        <span className="font-bold text-adv-primary">{r.domain}</span>
        <a
          href={r.url}
          target="_blank"
          rel="noopener noreferrer"
          className="adv-link block text-adv-body-s break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {r.url}
        </a>
      </div>
    ),
  },
  { key: 'impressions_count', label: 'Impressions', align: 'right', render: (r) => formatCount(r.impressions_count) },
  { key: 'clicks_count', label: 'Clicks', align: 'right', render: (r) => formatCount(r.clicks_count) },
  { key: 'ctr', label: 'CTR', align: 'right', render: (r) => formatPercent(r.ctr) },
  { key: 'viewability_rate', label: 'Viewability', align: 'right', render: (r) => formatPercent(r.viewability_rate) },
];

/**
 * Performance broken down by line item: groups `top_urls_by_line_item` by
 * `line_item_id` (line items already ordered by total impressions) and renders
 * each as a collapsible block with its KPIs and ranked top URLs.
 */
export function ReportLineItemPerformance({ topUrls, lineItems }: ReportLineItemPerformanceProps) {
  const kpisById = useMemo(() => {
    const map = new Map<string, LineItemMetricRow>();
    for (const li of lineItems ?? []) map.set(li.line_item_id, li);
    return map;
  }, [lineItems]);

  const groups = useMemo<LineItemGroup[]>(() => {
    const byId = new Map<string, LineItemGroup>();
    for (const row of topUrls) {
      let group = byId.get(row.line_item_id);
      if (!group) {
        group = {
          lineItemId: row.line_item_id,
          insertionOrderId: row.insertion_order_id,
          urls: [],
          totalImpressions: 0,
        };
        byId.set(row.line_item_id, group);
      }
      group.urls.push(row);
      group.totalImpressions += row.impressions_count || 0;
    }

    const list = Array.from(byId.values());
    for (const group of list) {
      group.urls.sort((a, b) => a.rank - b.rank);
    }
    // Preserve "ordered by total impressions" from the source data.
    list.sort((a, b) => b.totalImpressions - a.totalImpressions);
    return list;
  }, [topUrls]);

  if (groups.length === 0) {
    return <p className="text-adv-body-s text-adv-tertiary py-4">No per-line-item URL data</p>;
  }

  return (
    <div className="space-y-2">
      {groups.map((group, idx) => (
        <LineItemBlock
          key={group.lineItemId}
          group={group}
          kpis={kpisById.get(group.lineItemId)}
          defaultOpen={idx === 0}
        />
      ))}
    </div>
  );
}

function LineItemBlock({
  group,
  kpis,
  defaultOpen,
}: {
  group: LineItemGroup;
  kpis?: LineItemMetricRow;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-adv-border rounded-adv-lg bg-adv-card">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-adv-sunken rounded-adv-lg transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-adv-tertiary">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="min-w-0">
            <span className="font-bold text-adv-primary truncate block">
              Line item <span className="adv-num">{group.lineItemId}</span>
            </span>
            <span className="text-adv-body-s text-adv-tertiary">
              IO <span className="adv-num">{group.insertionOrderId}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-adv-body-s text-adv-secondary flex-shrink-0">
          <Stat label="Impr." value={formatCount(kpis?.impressions_count ?? group.totalImpressions)} />
          {kpis && <Stat label="CTR" value={formatPercent(kpis.ctr)} />}
          {kpis && <Stat label="View." value={formatPercent(kpis.viewability_rate)} />}
          {kpis && <Stat label="Conv." value={formatInt(kpis.total_conversions)} />}
          <span className="text-adv-tertiary">
            <span className="adv-num">{group.urls.length}</span> URLs
          </span>
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3">
          <ReportTable rows={group.urls} columns={urlColumns} initialLimit={10} />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="hidden sm:inline-flex flex-col items-end leading-tight">
      <span className="adv-kpi__label">{label}</span>
      <span className="adv-num font-bold text-adv-primary">{value}</span>
    </span>
  );
}
